import { useQuery } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';

import { api } from '../../services/client';
import { createMutationHook } from './createMutation';
import { buildQueryString } from '../../utils/buildQueryString';
import type {
  AdminBot,
  AdminBotListParams,
  AdminBotListResponse,
  BotPlatformState,
  BotToken,
  BotUpdate,
  BotUpdateStatus,
} from '../../types/bots';

// --- Query Key Factory ---
// all -> lists() -> list(params) | details() -> detail(id) -> updates(id, params); platform() is a sibling of lists/details.

export const botKeys = {
  all: ['bots'] as const,
  lists: () => [...botKeys.all, 'list'] as const,
  list: (params: AdminBotListParams) => [...botKeys.lists(), params] as const,
  details: () => [...botKeys.all, 'detail'] as const,
  detail: (id: string) => [...botKeys.details(), id] as const,
  updates: (
    id: string,
    params?: { status?: BotUpdateStatus; limit?: number },
  ) => [...botKeys.detail(id), 'updates', params ?? {}] as const,
  platform: () => [...botKeys.all, 'platform'] as const,
};

// --- Query Hooks ---

export const useBotsList = (params: AdminBotListParams) =>
  useQuery({
    queryKey: botKeys.list(params),
    queryFn: () =>
      api.get<AdminBotListResponse>(
        `/admin/bots?${buildQueryString({
          page: params.page,
          limit: params.limit,
          spaceId: params.spaceId || undefined,
          status: params.status || undefined,
          webhookState: params.webhookState || undefined,
          search: params.search || undefined,
        })}`,
      ),
    placeholderData: (previousData) => previousData,
  });

export const useBotDetail = (botId: string) =>
  useQuery({
    queryKey: botKeys.detail(botId),
    queryFn: () =>
      api.get<{ bot: AdminBot; tokens: BotToken[] }>(`/admin/bots/${botId}`),
    select: (res) => ({ ...res.bot, tokens: res.tokens }) as AdminBot,
    enabled: !!botId,
  });

export const useBotUpdates = (
  botId: string,
  params?: { status?: BotUpdateStatus; limit?: number },
) =>
  useQuery({
    queryKey: botKeys.updates(botId, params),
    queryFn: () =>
      api.get<BotUpdate[]>(
        `/admin/bots/${botId}/updates?${buildQueryString({
          status: params?.status || undefined,
          limit: params?.limit,
        })}`,
      ),
    enabled: !!botId,
  });

export const useBotPlatform = () =>
  useQuery({
    queryKey: botKeys.platform(),
    queryFn: () => api.get<BotPlatformState>('/admin/bots/platform'),
  });

// --- Mutation Hooks ---

export const useSetBotPlatform = createMutationHook<
  BotPlatformState,
  { enabled: boolean }
>({
  mutationFn: (vars) => api.put<BotPlatformState>('/admin/bots/platform', vars),
  invalidateKeys: [botKeys.platform()],
  successMessage: 'bots.toast.platformUpdated',
  errorMessage: 'common.operation_failed',
});

export const useDisableBot = createMutationHook<AdminBot, string>({
  mutationFn: (botId) => api.post<AdminBot>(`/admin/bots/${botId}/disable`),
  invalidateKeys: (botId) => [botKeys.lists(), botKeys.detail(botId)],
  successMessage: 'bots.toast.disabled',
  errorMessage: 'common.operation_failed',
});

export const useEnableBot = createMutationHook<AdminBot, string>({
  mutationFn: (botId) => api.post<AdminBot>(`/admin/bots/${botId}/enable`),
  invalidateKeys: (botId) => [botKeys.lists(), botKeys.detail(botId)],
  successMessage: 'bots.toast.enabled',
  errorMessage: 'common.operation_failed',
});

export const useDeleteBot = createMutationHook<{ success: boolean }, string>({
  mutationFn: (botId) => api.delete(`/admin/bots/${botId}`),
  invalidateKeys: (botId) => [botKeys.lists(), botKeys.detail(botId)],
  successMessage: 'bots.toast.deleted',
  errorMessage: 'common.operation_failed',
});

export const useRedeliverBotUpdate = createMutationHook<
  BotUpdate,
  { botId: string; updateId: string }
>({
  mutationFn: (vars) =>
    api.post<BotUpdate>(
      `/admin/bots/${vars.botId}/updates/${vars.updateId}/redeliver`,
    ),
  invalidateKeys: (vars) => [
    [...botKeys.detail(vars.botId), 'updates'] as QueryKey,
  ],
  successMessage: 'bots.toast.redelivered',
  errorMessage: 'common.operation_failed',
});
